import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemsCount, Table } from '@spotted-zebra-uk/ui';
import {
  useCompanyFindManyByIdsQuery,
  useKomboIntegrationsListQuery,
} from 'generated/graphql';
import { companyRoutes } from 'constants/navigation';
import { TABS_URL_VALUES } from 'views/Company/CompanyView/CompanyView.helpers';
import styles from './Kombo.module.scss';
import { INTEGRATION_COLUMNS, parseIntegrationRows } from './Kombo.helpers';

/* Far above today's integration count. The toolbar shows the true total from
   pageInfo, so a page beyond this cap reads as "N Companies" with the overflow
   rows absent — revisit with real pagination before that can happen. */
const PAGE_SIZE = 100;

/**
 * Every company with a Kombo integration, linking through to the company's
 * Integrations tab — the place all per-company Kombo settings live.
 */
export function Kombo() {
  const navigate = useNavigate();

  const { data, loading } = useKomboIntegrationsListQuery({
    variables: { skip: 0, take: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  const integrations = useMemo(
    () => data?.komboIntegrationsFindManyPaginated?.data ?? [],
    [data?.komboIntegrationsFindManyPaginated?.data]
  );
  /* The true company count, independent of the PAGE_SIZE cap on rows. */
  const totalCount =
    data?.komboIntegrationsFindManyPaginated?.pageInfo?.itemsTotal ??
    integrations.length;

  /* The Kombo integration model only carries a companyId, so the SZ company
     name for the Company column comes from a second lookup keyed on the ids
     in the current page. Skipped until the integrations have loaded. */
  const companyIds = useMemo(
    () => Array.from(new Set(integrations.map(({ companyId }) => companyId))),
    [integrations]
  );
  const { data: companiesData } = useCompanyFindManyByIdsQuery({
    variables: { ids: companyIds },
    skip: companyIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });
  const companyNames = useMemo(
    () =>
      new Map(
        (companiesData?.CompanyFindManyByIds ?? []).map(company => [
          company.id,
          company.name,
        ])
      ),
    [companiesData?.CompanyFindManyByIds]
  );

  const rows = useMemo(
    () => parseIntegrationRows(integrations, companyNames),
    [integrations, companyNames]
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Kombo</h2>
        <p className={styles.caption}>
          Companies with a Kombo ATS or assessment integration. Select one to
          open its Integrations tab.
        </p>
      </div>

      {!loading && integrations.length === 0 ? (
        <p className={styles.emptyState}>
          No company has a Kombo integration yet. One appears here once a
          company connects its ATS or assessment tool through Kombo.
        </p>
      ) : (
        <Table
          className={styles.table}
          rows={rows}
          columns={INTEGRATION_COLUMNS}
          loading={loading}
          rowCount={rows.length}
          // rows may be capped at PAGE_SIZE; the toolbar count is the true total
          onRowClick={params =>
            navigate(
              `${companyRoutes.editCompany.url(
                params.row.companyId
              )}?activeTab=${TABS_URL_VALUES.Integrations}`
            )
          }
          disableColumnFilter
          hasToolbar
          slotProps={{
            toolbar: {
              leftMainAction: (
                <ItemsCount count={totalCount} text="Companies" />
              ),
            },
          }}
        />
      )}
    </div>
  );
}

export default Kombo;
